// pages/NearbyServicesPage.jsx
import React, { useState } from 'react';
import { MapPin, Navigation, Phone, Clock, Star, Filter, ChevronRight, Crosshair } from 'lucide-react';

const NearbyServicesPage = () => {
  const [selectedService, setSelectedService] = useState('all');
  const [userLocation, setUserLocation] = useState({ lat: 40.7128, lng: -74.0060 });
  const [range, setRange] = useState(5);

  const services = [
    {
      id: 1,
      name: 'City General Hospital',
      type: 'hospital',
      distance: 1.2,
      rating: 4.8,
      reviews: 1247,
      address: '123 Medical Center Dr, New York, NY',
      phone: '(555) 123-4567',
      hours: '24/7 Emergency',
      waitTime: '15-30 min',
      coordinates: { lat: 40.715, lng: -74.008 },
      services: ['Emergency', 'Surgery', 'ICU', 'Pharmacy'],
      isOpen: true
    },
    // ... other services (same as before)
  ];

  const pageStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem'
  };

  const cardStyles = {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    border: '1px solid #e5e7eb',
    padding: '1.5rem'
  };

  const primaryButtonStyles = {
    backgroundColor: '#4f46e5',
    color: '#ffffff',
    padding: '0.5rem 1rem',
    borderRadius: '8px',
    border: 'none',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '0.875rem'
  };

  const filteredServices = services.filter(service => {
    if (selectedService === 'all') return service.distance <= range;
    return service.type === selectedService && service.distance <= range;
  });

  return (
    <div style={pageStyles}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
        <MapPin style={{ marginRight: '0.75rem' }} size={24} color="#7B61FF" />
        <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#111827' }}>
          Nearby Medical Services
        </h1>
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr',
        gap: '2rem'
      }}>
        {/* Left Column - Filters and List */}
        <div style={{ gridColumn: 'span 2' }}>
          {/* Location and Range Selector */}
          <div style={cardStyles}>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: '1rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Crosshair size={20} style={{ color: '#4f46e5' }} />
                    <span style={{ fontWeight: '500' }}>Your Location</span>
                  </div>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    {userLocation.lat.toFixed(4)}, {userLocation.lng.toFixed(4)}
                  </p>
                </div>
                <button
                  onClick={() => {}}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #c7d2fe',
                    color: '#4f46e5',
                    borderRadius: '6px',
                    backgroundColor: 'transparent',
                    cursor: 'pointer'
                  }}
                >
                  <Navigation size={16} style={{ marginRight: '0.5rem' }} />
                  Update Location
                </button>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Range: {range} km</span>
                <input
                  type="range"
                  min="1"
                  max="20"
                  value={range}
                  onChange={(e) => setRange(parseInt(e.target.value))}
                  style={{ width: '8rem' }}
                />
              </div>
            </div>
          </div>

          {/* Services List */}
          <div style={{ marginTop: '1.5rem' }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
              Available Services
            </h2>
            
            {filteredServices.length > 0 ? (
              filteredServices.map((service) => (
                <div
                  key={service.id}
                  style={{
                    ...cardStyles,
                    marginBottom: '1rem',
                    transition: 'box-shadow 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
                  }}
                >
                  <div style={{ padding: '1.5rem' }}>
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: '1rem'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#111827' }}>
                              {service.name}
                            </h3>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '9999px',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              backgroundColor: service.isOpen ? '#d1fae5' : '#fee2e2',
                              color: service.isOpen ? '#065f46' : '#991b1b'
                            }}>
                              {service.isOpen ? 'OPEN NOW' : 'CLOSED'}
                            </span>
                          </div>
                          
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <Star size={16} style={{ color: '#fbbf24', marginRight: '0.25rem' }} />
                              <span style={{ fontWeight: '500' }}>{service.rating}</span>
                              <span style={{ fontSize: '0.875rem', color: '#6b7280', marginLeft: '0.25rem' }}>
                                ({service.reviews} reviews)
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', color: '#4b5563' }}>
                              <MapPin size={16} style={{ marginRight: '0.25rem' }} />
                              <span>{service.distance} km away</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', color: '#4b5563' }}>
                              <Clock size={16} style={{ marginRight: '0.25rem' }} />
                              <span>{service.waitTime} wait</span>
                            </div>
                          </div>
                        </div>
                        
                        <div style={{ marginTop: '1rem' }}>
                          <button
                            onClick={() => {}}
                            style={primaryButtonStyles}
                          >
                            <Navigation size={16} style={{ marginRight: '0.5rem' }} />
                            Directions
                          </button>
                        </div>
                      </div>
                      
                      <div style={{ marginTop: '1rem' }}>
                        <p style={{ color: '#4b5563', display: 'flex', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                          <MapPin size={16} style={{ marginRight: '0.5rem', marginTop: '0.125rem' }} />
                          {service.address}
                        </p>
                        <p style={{ color: '#4b5563', display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <Phone size={16} style={{ marginRight: '0.5rem' }} />
                          {service.phone}
                        </p>
                        <p style={{ color: '#4b5563' }}>{service.hours}</p>
                      </div>
                      
                      <div style={{ marginTop: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {service.services.map((srv, idx) => (
                          <span
                            key={idx}
                            style={{
                              padding: '0.25rem 0.75rem',
                              backgroundColor: '#f3f4f6',
                              color: '#374151',
                              borderRadius: '9999px',
                              fontSize: '0.875rem'
                            }}
                          >
                            {srv}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div style={{ 
                      marginTop: '1.5rem', 
                      paddingTop: '1.5rem', 
                      borderTop: '1px solid #f3f4f6',
                      display: 'flex', 
                      justifyContent: 'flex-end', 
                      gap: '1rem' 
                    }}>
                      <button style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '0.5rem 1rem',
                        border: '1px solid #d1d5db',
                        color: '#374151',
                        borderRadius: '8px',
                        backgroundColor: 'transparent',
                        cursor: 'pointer'
                      }}>
                        <Phone size={16} style={{ marginRight: '0.5rem' }} />
                        Call Now
                      </button>
                      <button style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '0.5rem 1rem',
                        border: 'none',
                        color: '#ffffff',
                        backgroundColor: '#059669',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}>
                        Book Appointment
                        <ChevronRight size={16} style={{ marginLeft: '0.5rem' }} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ 
                ...cardStyles,
                padding: '3rem',
                textAlign: 'center'
              }}>
                <MapPin size={48} style={{ margin: '0 auto', color: '#9ca3af' }} />
                <h3 style={{ marginTop: '1rem', fontSize: '1.125rem', fontWeight: '500', color: '#111827' }}>
                  No services found
                </h3>
                <p style={{ marginTop: '0.5rem', color: '#6b7280' }}>
                  Try increasing the range or selecting a different service type
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NearbyServicesPage;